/**
 * Filtratsiya "turg'un" hisobi — tsikl xarakteristikalari (analitik).
 *
 * Filtratsiya vaqt bo'yicha jarayon, turg'un holati yo'q; `steady` berilgan yakuniy
 * hajm (inputs.targetVolume) yoki cho'kma qalinligi (inputs.targetCakeThickness),
 * ikkalasi ham bo'lmasa — L_max (kamera to'lishi) uchun tsiklni hisoblaydi:
 *
 *   constantPressure: t = (V² + 2·C·V)/K  (Ruth), K, C ΔP da; Q_end = A²ΔP/(μ(αcV + R_mA))
 *   constantRate:     ΔP_end = Qμ(α(ΔP_end)cV + R_mA)/A²;  ΔP_end ≤ ΔP_max → t = V/Q;
 *                     aks holda V_lim gacha o'zgarmas Q (t₁ = V_lim/Q), so'ng ΔP_max da
 *                     Ruth: t₂ = [(V² + 2CV) − (V_lim² + 2CV_lim)]/K  (K, C ΔP_max da);
 *                     `filtration.pressureLimit`.
 *   Maqsad hajm V_full dan katta bo'lsa → V = V_full, `filtration.cakeFull` (critical).
 *   averageRate = V/t — tsiklning o'rtacha unumdorligi (yuvish/tushirish vaqtisiz).
 *
 * Chiqish `t` maydoni — tsikl davomiyligi (dinamikadagi joriy vaqt o'rniga).
 *
 * MANBA: Kasatkin (filtrlash tsikli hisobi); PRN (filtr-press misollari).
 */
import { warn, type SteadyResult, type Warning } from '../../types';
import {
  DEFAULT_TEMPERATURE,
  cakeThickness,
  flowAt,
  pressureForRate,
  ruthConstants,
  ruthTime,
  specificResistance,
  validateFiltrationInputs,
  validateFiltrationParams,
  viscosityOf,
  volumeForThickness,
} from './ruth';
import type { FiltrationInputs, FiltrationOutputs, FiltrationParams } from './types';

export function solveSteady(
  p: FiltrationParams,
  inputs: FiltrationInputs,
): SteadyResult<FiltrationOutputs> {
  validateFiltrationParams(p);
  validateFiltrationInputs(p, inputs);
  const warnings: Warning[] = [];
  const mu = viscosityOf(p, inputs.temperature ?? DEFAULT_TEMPERATURE, warnings);
  const c = inputs.solidsConcentration;
  const Vfull = volumeForThickness(p, c, p.maxCakeThickness);

  let Vt: number;
  if (inputs.targetVolume !== undefined) Vt = inputs.targetVolume;
  else if (inputs.targetCakeThickness !== undefined)
    Vt = volumeForThickness(p, c, inputs.targetCakeThickness);
  else Vt = Vfull;
  if (!Number.isFinite(Vt)) throw new Error('filtration: targetVolume is required when c = 0');

  let cakeFull = false;
  if (Vt >= Vfull) {
    Vt = Vfull;
    cakeFull = Vt >= Vfull;
    warn(warnings, 'filtration.cakeFull', 'critical');
  }
  if (p.mediumResistance <= 0 && c <= 0) warn(warnings, 'filtration.noResistance', 'critical');

  let t: number;
  let dP: number;
  let Q: number;
  let pressureLimited = false;

  if (p.mode === 'constantPressure') {
    dP = inputs.pressureDrop as number;
    const alpha = specificResistance(p, dP);
    if (dP <= 0) warn(warnings, 'filtration.noDrivingForce', 'warning');
    t = ruthTime(p, mu, alpha, c, dP, Vt);
    Q = cakeFull ? 0 : flowAt(p, mu, alpha, c, Vt, dP);
  } else {
    const Qset = inputs.flowRate as number;
    const Pmax = p.maxPressure as number;
    if (Qset <= 0) {
      warn(warnings, 'filtration.noDrivingForce', 'warning');
      dP = 0;
      t = Vt > 0 ? Infinity : 0;
      Q = 0;
    } else {
      const dPend = pressureForRate(p, mu, c, Vt, Qset);
      if (dPend <= Pmax) {
        dP = dPend;
        t = Vt / Qset;
        Q = cakeFull ? 0 : Qset;
      } else {
        pressureLimited = true;
        warn(warnings, 'filtration.pressureLimit', 'warning', { dPmax: Pmax });
        dP = Pmax;
        const aMax = specificResistance(p, Pmax);
        const A = p.area;
        const Vlim = Math.max(
          0,
          c > 0 ? ((Pmax * A * A) / (Qset * mu) - p.mediumResistance * A) / (aMax * c) : 0,
        );
        t = Vlim / Qset + ruthTime(p, mu, aMax, c, Pmax, Vt) - ruthTime(p, mu, aMax, c, Pmax, Vlim);
        Q = cakeFull ? 0 : flowAt(p, mu, aMax, c, Vt, Pmax);
      }
    }
  }

  const alpha = specificResistance(p, dP);
  const { K, C } = ruthConstants(p, mu, alpha, c, dP);
  return {
    outputs: {
      t,
      V: Vt,
      Q: Math.min(Q, Number.MAX_VALUE),
      cakeThickness: cakeThickness(p, c, Vt),
      dP,
      K,
      C,
      averageRate: t > 0 && Number.isFinite(t) ? Vt / t : 0,
      viscosity: mu,
      specificCakeResistance: alpha,
      cakeFull,
      pressureLimited,
    },
    warnings,
    diagnostics: { converged: true, iterations: 0, residual: 0 },
  };
}
